import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { IonIcon } from '@ionic/react';
import { eye, eyeOff } from 'ionicons/icons';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Form, Button, Card, InputGroup, Alert, Row, Col } from 'react-bootstrap';
import { TIER_LABELS } from '../constants/subscriptionTiers';

const SettingsPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [subTier, setSubTier] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { token, setUser } = useContext(AuthContext);
  const { toast } = useToast();
  const navigate = useNavigate();

  const passwordsMatch = newPassword === confirmNewPassword && newPassword.length > 0;

  useEffect(() => {
    if (!token) {
      navigate('/app');
      return;
    }

    const loadUserData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userData = response.data;
        setFirstName(userData.firstName || '');
        setLastName(userData.lastName || '');
        setEmail(userData.email || '');
        setSubTier(userData.subTier || 'free');
      } catch (err) {
        setError('Failed to load user data');
      }
    };

    loadUserData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const updateData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
    };

    if (newPassword) {
      if (!currentPassword) {
        setError('Current password is required to change password');
        setLoading(false);
        return;
      }
      if (!passwordsMatch) {
        setError('New passwords do not match');
        setLoading(false);
        return;
      }
      updateData.currentPassword = currentPassword;
      updateData.newPassword = newPassword;
    }

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL || ''}/api/user/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update profile';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <Row className="g-4">
      <Col lg={6}>
        <Card className="app-surface-card h-100">
          <Card.Body>
            <Card.Title style={{ fontFamily: 'var(--font-display)' }}>Profile</Card.Title>
            {subTier && (
              <div className="mb-3">
                <span className="app-tier-badge">{TIER_LABELS[subTier] || subTier}</span>
              </div>
            )}
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Button type="submit" variant="secondary" disabled={loading}>
                {loading ? 'Updating...' : 'Save Profile'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={6}>
        <Card className="app-surface-card h-100">
          <Card.Body>
            <Card.Title style={{ fontFamily: 'var(--font-display)' }}>Security</Card.Title>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <InputGroup.Text onClick={() => setShowPassword(!showPassword)} className="peek-toggle">
                    <IonIcon icon={showPassword ? eyeOff : eye} />
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={confirmNewPassword ? (passwordsMatch ? 'match' : 'mismatch') : ''}
                  />
                  <InputGroup.Text onClick={() => setShowPassword(!showPassword)} className="peek-toggle">
                    <IonIcon icon={showPassword ? eyeOff : eye} />
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className={confirmNewPassword ? (passwordsMatch ? 'match' : 'mismatch') : ''}
                />
              </Form.Group>
              <Button
                type="submit"
                variant="secondary"
                disabled={loading || (newPassword && !passwordsMatch)}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default SettingsPage;